import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api, type EstadoPortal } from '@/lib/api';
import { Owner, Property, OwnerDocument } from '@/lib/types';
import { ContratoLigero, ESTADO_CONTRATO, NOMBRE_MODELO } from '@/lib/contratos';
import { formatDate, formatCurrency, SOURCE_LABELS, LANGUAGE_LABELS, ZONE_LABELS, getInitials } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import DocumentSection from '@/components/owners/DocumentSection';
import OwnerLiquidaciones from '@/components/owners/OwnerLiquidaciones';
import BloqueTareas from '@/components/tareas/BloqueTareas';
import { ConfirmarActivacion, EstadoAcceso } from '@/components/owners/PortalAcceso';
import ProcesarContrato from '@/components/owners/ProcesarContrato';
import {
  ArrowLeft, Trash2, Plus, Phone, Mail, MessageCircle,
  Building2, MapPin, Globe, FileText, Search,
  FileSignature, Unlink, Eye, Download, XCircle, Loader2, Pencil, Save, X,
  KeyRound, RotateCcw, Copy, Check, EyeOff, ExternalLink,
  User, Users, Receipt, FolderOpen, Wallet, CheckSquare, ScanText
} from 'lucide-react';

type TabKey = 'info' | 'tareas' | 'propiedades' | 'contratos' | 'facturacion' | 'liquidaciones' | 'documentos' | 'portal';

export default function OwnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'info';
  const setActiveTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);
  const [propertySearch, setPropertySearch] = useState('');
  const [unassignedProperties, setUnassignedProperties] = useState<Property[]>([]);
  const [assigningProperty, setAssigningProperty] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Inline edit sections
  const [editSection, setEditSection] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});

  // Contracts
  const [ownerContracts, setOwnerContracts] = useState<any[]>([]);
  const [showContractPicker, setShowContractPicker] = useState(false);
  const [contractSearch, setContractSearch] = useState('');
  const [availableContracts, setAvailableContracts] = useState<any[]>([]);
  const [linkingContract, setLinkingContract] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [pdfViewer, setPdfViewer] = useState<{ signatureId: string; filename: string } | null>(null);
  // «Procesar contrato»: firma abierta y si se guardó algo, para recargar la ficha al cerrar
  // (recargar con la ventana abierta la desmonta: la página entera vuelve al spinner).
  const [procesando, setProcesando] = useState<string | null>(null);
  const [procesadoGuardado, setProcesadoGuardado] = useState(false);
  const [ownerDocs, setOwnerDocs] = useState<OwnerDocument[]>([]);

  // Contratos generados desde el CRM (Clientes › Contratos › Generar). Los de
  // Click & Sign vinculados a mano siguen en `ownerContracts`.
  const [contratosGenerados, setContratosGenerados] = useState<ContratoLigero[]>([]);
  useEffect(() => {
    if (!id) return;
    api.getContratos({ parteId: id }).then(setContratosGenerados).catch(() => setContratosGenerados([]));
  }, [id]);

  // Liquidaciones del propietario (las mismas de Contabilidad > Liquidaciones).
  // Se cargan al entrar y no al abrir la pestaña porque el contador del rótulo
  // las necesita; la consulta va por ownerId, que es la clave de partición.
  const [ownerLiquidaciones, setOwnerLiquidaciones] = useState<any[]>([]);
  const [loadingLiquidaciones, setLoadingLiquidaciones] = useState(true);

  // Portal del propietario
  const [portalStatus, setPortalStatus] = useState<EstadoPortal>({ enabled: false, email: null });
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalPassword, setPortalPassword] = useState<string | null>(null);
  const [portalShowPassword, setPortalShowPassword] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);
  const [portalCustomPassword, setPortalCustomPassword] = useState('');
  const [portalShowCustomInput, setPortalShowCustomInput] = useState(false);

  const { data: owner, loading, refresh } = useApi<Owner>(() => api.getOwner(id!), [id]);
  const { data: properties, refresh: refreshProperties } = useApi<Property[]>(() => api.getProperties({ ownerId: id! }), [id]);

  const recargarPortal = async () => {
    if (!id) return;
    await api.getPortalStatus(id).then(setPortalStatus).catch(() => setPortalStatus({ enabled: false, email: null }));
  };

  // Load owner's linked contracts from CosmosDB (no Click & Sign calls)
  useEffect(() => {
    if (id) {
      api.getOwnerContracts(id).then(setOwnerContracts).catch(() => setOwnerContracts([]));
      api.getDocuments(id).then(setOwnerDocs).catch(() => setOwnerDocs([]));
      recargarPortal();
      setLoadingLiquidaciones(true);
      api.getLiquidaciones({ ownerId: id })
        .then(setOwnerLiquidaciones)
        .catch(() => setOwnerLiquidaciones([]))
        .finally(() => setLoadingLiquidaciones(false));
    }
  }, [id]);

  if (loading || !owner) return <LoadingSpinner />;

  const refreshDocs = () => {
    api.getDocuments(id!).then(setOwnerDocs).catch(() => setOwnerDocs([]));
  };

  const handleUpdate = async (data: any) => {
    setSaving(true);
    try {
      await api.updateOwner(id!, data);
      setEditSection(null);
      refresh();
    } catch {
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (section: string, initialData: Record<string, any>) => {
    setEditForm(initialData);
    setEditSection(section);
  };

  const cancelEdit = () => {
    setEditSection(null);
    setEditForm({});
  };

  const saveSection = () => {
    const data: Record<string, any> = { ...editForm };
    if (data.incomeExpectation) data.incomeExpectation = Number(data.incomeExpectation);
    if (data.tags && typeof data.tags === 'string') data.tags = data.tags.split(',').map((t: string) => t.trim()).filter(Boolean);
    if (data.invoiceNextNum !== undefined) data.invoiceNextNum = data.invoiceNextNum === '' ? undefined : Number(data.invoiceNextNum);
    if (data.commissionPercent !== undefined) data.commissionPercent = data.commissionPercent === '' ? undefined : Number(data.commissionPercent);
    if (data.fixedRent !== undefined) data.fixedRent = data.fixedRent === '' ? undefined : Number(data.fixedRent);
    if (data.secondOwnerPercent !== undefined) data.secondOwnerPercent = data.secondOwnerPercent === '' ? 50 : Number(data.secondOwnerPercent);
    handleUpdate(data);
  };

  const handleDelete = async () => {
    if (confirm('¿Eliminar este propietario?')) {
      await api.deleteOwner(id!);
      navigate('/owners');
    }
  };

  const openPropertyPicker = async () => {
    setShowPropertyPicker(true);
    setPropertySearch('');
    setLoadingProperties(true);
    try {
      const all = await api.getProperties({});
      setUnassignedProperties(all.filter((p: Property) => {
        if (p.ownerId && p.ownerId !== '') return false;
        const svc = p.hostifyData?.servicePms;
        return svc === undefined || svc === null || svc === 1;
      }));
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleSearchProperties = async (q: string) => {
    setPropertySearch(q);
    const all = await api.getProperties(q ? { search: q } : {});
    setUnassignedProperties(all.filter((p: Property) => {
      if (p.ownerId && p.ownerId !== '') return false;
      const svc = p.hostifyData?.servicePms;
      return svc === undefined || svc === null || svc === 1;
    }));
  };

  const handleAssignProperty = async (propertyId: string) => {
    setAssigningProperty(true);
    try {
      await api.updateProperty(propertyId, { ownerId: id });
      setShowPropertyPicker(false);
      refreshProperties();
    } catch {
      alert('Error al asignar la propiedad');
    } finally {
      setAssigningProperty(false);
    }
  };

  const handleUnassignProperty = async (propertyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('¿Desasociar esta propiedad del propietario?')) return;
    try {
      await api.updateProperty(propertyId, { ownerId: '' });
      refreshProperties();
    } catch {
      alert('Error al desasociar la propiedad');
    }
  };

  const openContractPicker = async () => {
    setShowContractPicker(true);
    setContractSearch('');
    setLoadingContracts(true);
    try {
      const [sigs, links] = await Promise.all([
        api.getAllSignatures(),
        api.getContractLinks(),
      ]);
      const linkedSigIds = new Set(Object.keys(links));
      setAvailableContracts(sigs.filter((s: any) =>
        s.signature_status === 'signed' && !linkedSigIds.has(String(s.signature_id))
      ));
    } finally {
      setLoadingContracts(false);
    }
  };

  const handleLinkContract = async (sig: any) => {
    setLinkingContract(true);
    try {
      const ownerName = `${owner.firstName} ${owner.lastName}`;
      await api.linkContract(String(sig.signature_id), id!, ownerName, {
        contractId: sig.contract_id || null,
        configName: sig.config_name || null,
        signatureStatus: sig.signature_status || null,
      });
      setShowContractPicker(false);
      // Reload from CosmosDB (single lightweight call)
      const contracts = await api.getOwnerContracts(id!);
      setOwnerContracts(contracts);
    } catch {
      alert('Error al vincular el contrato');
    } finally {
      setLinkingContract(false);
    }
  };

  const handleUnlinkContract = async (signatureId: string) => {
    if (!confirm('¿Desvincular este contrato?')) return;
    try {
      await api.unlinkContract(signatureId);
      setOwnerContracts(prev => prev.filter(c => String(c.signature_id) !== String(signatureId)));
    } catch {
      alert('Error al desvincular el contrato');
    }
  };

  const cerrarProcesado = (verFicha = false) => {
    setProcesando(null);
    if (verFicha) setActiveTab('info');
    if (procesadoGuardado) {
      setProcesadoGuardado(false);
      refresh();
      refreshProperties();
      refreshDocs();
    }
  };

  // El visor y la descarga van contra el proxy del backend, que resuelve el
  // documento en Click & Sign; el enlace directo del proveedor caduca.
  const handleViewContract = (sig: any) => {
    setPdfViewer({
      signatureId: String(sig.signature_id),
      filename: `${sig.contract_id || `contrato-${sig.signature_id}`}.pdf`,
    });
  };

  return (
    <div>
      <button onClick={() => navigate('/owners')} className="btn-ghost mb-4 -ml-4">
        <ArrowLeft size={16} className="inline mr-1" /> Propietarios
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-sereno-dark text-white flex items-center justify-center text-base sm:text-lg font-medium flex-shrink-0">
            {getInitials(owner.firstName, owner.lastName)}
          </div>
          <div>
            {editSection === 'name' ? (
              <div className="flex items-center gap-2">
                <input
                  className="input text-base sm:text-lg font-bold py-1 w-32 sm:w-40"
                  value={editForm.firstName}
                  onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                  placeholder="Nombre"
                  autoFocus
                />
                <input
                  className="input text-base sm:text-lg font-bold py-1 w-36 sm:w-48"
                  value={editForm.lastName}
                  onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))}
                  placeholder="Apellidos"
                />
                <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={15} /></button>
                <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold">{owner.firstName} {owner.lastName}</h1>
                <button
                  onClick={() => startEdit('name', { firstName: owner.firstName || '', lastName: owner.lastName || '' })}
                  className="p-1 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors"
                  title="Editar nombre"
                >
                  <Pencil size={14} />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className={`badge ${owner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {owner.status === 'active' ? 'Activo' : 'Inactivo'}
              </span>
              {(Array.isArray(owner.tags) ? owner.tags : []).map(tag => (
                <span key={tag} className="badge bg-gray-100 text-gray-600">{tag}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={handleDelete} className="btn-danger flex items-center gap-1">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
        <nav className="flex gap-1 sm:gap-2 min-w-max">
          {([
            { key: 'info', label: 'Información', icon: User },
            { key: 'tareas', label: 'Tareas', icon: CheckSquare },
            { key: 'propiedades', label: 'Propiedades', icon: Building2, count: properties?.length || 0 },
            { key: 'contratos', label: 'Contratos', icon: FileSignature, count: ownerContracts.length },
            { key: 'facturacion', label: 'Facturación', icon: Receipt },
            { key: 'liquidaciones', label: 'Liquidaciones', icon: Wallet, count: ownerLiquidaciones.length },
            { key: 'documentos', label: 'Documentos', icon: FolderOpen, count: ownerDocs.filter(d => d.category === 'licencia-turistica' || d.category === 'otros').length },
            { key: 'portal', label: 'Portal', icon: KeyRound },

          ] as { key: TabKey; label: string; icon: any; count?: number }[]).map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-sereno-dark text-sereno-dark'
                    : 'border-transparent text-sereno-dark/40 hover:text-sereno-dark/70 hover:border-gray-300'
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/50'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="space-y-6">
        {/* === TAB: INFORMACIÓN === */}
        {activeTab === 'info' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Contacto</h3>
              {editSection !== 'contact' ? (
                <button onClick={() => startEdit('contact', {
                  email: owner.email || '', emailSecondary: owner.emailSecondary || '',
                  phone: owner.phone || '', phoneSecondary: owner.phoneSecondary || '',
                  whatsapp: owner.whatsapp || '', preferredLanguage: owner.preferredLanguage || 'es',
                  dni: owner.dni || '', iban: owner.iban || '', fiscalAddress: owner.fiscalAddress || '',
                  address: { street: owner.address?.street || '', city: owner.address?.city || '', province: owner.address?.province || 'Cádiz', postalCode: owner.address?.postalCode || '' },
                })} className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors">
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              )}
            </div>
            {editSection !== 'contact' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  {owner.email ? <a href={`mailto:${owner.email}`} className="hover:underline">{owner.email}</a> : <span className="text-sereno-dark/25">Sin email</span>}
                </div>
                {owner.emailSecondary && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={14} className="text-sereno-dark/40 flex-shrink-0" />
                    <a href={`mailto:${owner.emailSecondary}`} className="hover:underline">{owner.emailSecondary}</a>
                    <span className="text-sereno-dark/30 text-xs">(secundario)</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  {owner.phone ? (
                    <>
                      <a href={`tel:${owner.phone}`} className="hover:underline">{owner.phone}</a>
                      <a href={`https://wa.me/${owner.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700" title="WhatsApp"><MessageCircle size={14} /></a>
                    </>
                  ) : <span className="text-sereno-dark/25">Sin teléfono</span>}
                </div>
                {owner.phoneSecondary && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-sereno-dark/40 flex-shrink-0" />
                    <span>{owner.phoneSecondary} <span className="text-sereno-dark/30 text-xs">(secundario)</span></span>
                  </div>
                )}
                {owner.whatsapp && owner.whatsapp !== owner.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <MessageCircle size={14} className="text-sereno-dark/40 flex-shrink-0" />
                    <a href={`https://wa.me/${owner.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-green-600">WhatsApp: {owner.whatsapp}</a>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Globe size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  <span>{LANGUAGE_LABELS[owner.preferredLanguage] || owner.preferredLanguage || <span className="text-sereno-dark/25">Sin idioma</span>}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  <span>{owner.dni || <span className="text-sereno-dark/25">Sin DNI/NIE</span>}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  <span className="font-mono">{owner.iban || <span className="text-sereno-dark/25 font-sans">Sin IBAN</span>}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-sereno-dark/40 flex-shrink-0" />
                  {owner.address?.street ? <span>{owner.address.street}, {owner.address.city} {owner.address.postalCode}</span> : <span className="text-sereno-dark/25">Sin dirección</span>}
                </div>
                {owner.fiscalAddress && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={14} className="text-sereno-dark/40 flex-shrink-0" />
                    <span>Fiscal: {owner.fiscalAddress}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-sereno-dark/40">Email</label><input type="email" className="input text-sm" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></div>
                  <div><label className="text-xs text-sereno-dark/40">Email secundario</label><input type="email" className="input text-sm" value={editForm.emailSecondary} onChange={e => setEditForm(f => ({ ...f, emailSecondary: e.target.value }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-sereno-dark/40">Teléfono</label><input className="input text-sm" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} /></div>
                  <div><label className="text-xs text-sereno-dark/40">Tel. secundario</label><input className="input text-sm" value={editForm.phoneSecondary} onChange={e => setEditForm(f => ({ ...f, phoneSecondary: e.target.value }))} /></div>
                </div>
                <div><label className="text-xs text-sereno-dark/40">WhatsApp</label><input className="input text-sm" value={editForm.whatsapp} onChange={e => setEditForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="+34 600..." /></div>
                <div><label className="text-xs text-sereno-dark/40">Idioma</label><select className="input text-sm" value={editForm.preferredLanguage} onChange={e => setEditForm(f => ({ ...f, preferredLanguage: e.target.value }))}>{Object.entries(LANGUAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-sereno-dark/40">DNI/NIE/CIF</label><input className="input text-sm" value={editForm.dni} onChange={e => setEditForm(f => ({ ...f, dni: e.target.value }))} /></div>
                  <div><label className="text-xs text-sereno-dark/40">IBAN</label><input className="input text-sm" value={editForm.iban} onChange={e => setEditForm(f => ({ ...f, iban: e.target.value }))} /></div>
                </div>
                <div><label className="text-xs text-sereno-dark/40">Dirección</label><input className="input text-sm" value={editForm.address?.street || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, street: e.target.value } }))} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-sereno-dark/40">Ciudad</label><input className="input text-sm" value={editForm.address?.city || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))} /></div>
                  <div><label className="text-xs text-sereno-dark/40">CP</label><input className="input text-sm" value={editForm.address?.postalCode || ''} onChange={e => setEditForm(f => ({ ...f, address: { ...f.address, postalCode: e.target.value } }))} /></div>
                </div>
                <div><label className="text-xs text-sereno-dark/40">Dirección fiscal</label><input className="input text-sm" value={editForm.fiscalAddress} onChange={e => setEditForm(f => ({ ...f, fiscalAddress: e.target.value }))} placeholder="Solo si es diferente" /></div>
              </div>
            )}
          </div>

          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Detalles</h3>
              {editSection !== 'details' ? (
                <button onClick={() => startEdit('details', {
                  contractModel: owner.contractModel || '', source: owner.source || '', sourceDetail: owner.sourceDetail || '',
                  status: owner.status || 'active', hasMultipleProperties: owner.hasMultipleProperties || false,
                  previouslyManagedRentals: owner.previouslyManagedRentals || false, previousManagementDetail: owner.previousManagementDetail || '',
                  incomeExpectation: owner.incomeExpectation || '', incomeExpectationPeriod: owner.incomeExpectationPeriod || 'monthly',
                  tags: (Array.isArray(owner.tags) ? owner.tags : []).join(', '),
                  applyIrpf: owner.applyIrpf ?? false,
                })} className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors">
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              )}
            </div>
            {editSection !== 'details' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-sereno-dark/50">Estado</span><span className={`badge ${owner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{owner.status === 'active' ? 'Activo' : 'Inactivo'}</span></div>
                <div className="flex justify-between"><span className="text-sereno-dark/50">Modelo contrato</span><span>{owner.contractModel ? ({ gestion: 'Gestión', explotacion: 'Explotación', explotacion_lcm: 'Explotación LCM', explotacion_sin_irpf: 'Explotación s/IRPF', rent2rent: 'Rent2Rent', ambos: 'Ambos' } as Record<string, string>)[owner.contractModel] : <span className="text-sereno-dark/25">Sin definir</span>}</span></div>
                {(owner.contractModel === 'explotacion' || owner.contractModel === 'explotacion_lcm') && (
                  <div className="flex justify-between"><span className="text-sereno-dark/50">Retención IRPF</span><span className={owner.applyIrpf ? 'text-amber-700 font-medium' : 'text-sereno-dark/25'}>{owner.applyIrpf ? '19%' : 'No aplica'}</span></div>
                )}
                <div className="flex justify-between"><span className="text-sereno-dark/50">Fuente</span><span>{SOURCE_LABELS[owner.source] || owner.source || <span className="text-sereno-dark/25">Sin fuente</span>}</span></div>
                {owner.sourceDetail && <div className="flex justify-between"><span className="text-sereno-dark/50">Detalle fuente</span><span className="text-right max-w-[60%]">{owner.sourceDetail}</span></div>}
                <div className="flex justify-between"><span className="text-sereno-dark/50">Múltiples propiedades</span><span>{owner.hasMultipleProperties ? 'Sí' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-sereno-dark/50">Gestionaba antes</span><span>{owner.previouslyManagedRentals ? 'Sí' : 'No'}</span></div>
                {owner.previousManagementDetail && <div className="flex justify-between"><span className="text-sereno-dark/50">Detalle gestión</span><span className="text-right max-w-[60%]">{owner.previousManagementDetail}</span></div>}
                <div className="flex justify-between"><span className="text-sereno-dark/50">Expectativa</span><span>{owner.incomeExpectation ? `${formatCurrency(owner.incomeExpectation)}/${owner.incomeExpectationPeriod === 'monthly' ? 'mes' : 'año'}` : <span className="text-sereno-dark/25">Sin definir</span>}</span></div>
                <div className="flex justify-between"><span className="text-sereno-dark/50">Etiquetas</span><span>{Array.isArray(owner.tags) && owner.tags.length ? owner.tags.join(', ') : <span className="text-sereno-dark/25">-</span>}</span></div>
                <div className="flex justify-between"><span className="text-sereno-dark/50">Creado</span><span>{formatDate(owner.createdAt)}</span></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div><label className="text-xs text-sereno-dark/40">Estado</label><select className="input text-sm" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}><option value="active">Activo</option><option value="inactive">Inactivo</option></select></div>
                <div>
                  <label className="text-xs text-sereno-dark/40">Modelo contrato</label>
                  <select className="input text-sm" value={editForm.contractModel} onChange={e => {
                    const val = e.target.value;
                    setEditForm(f => ({ ...f, contractModel: val, applyIrpf: (val === 'explotacion' || val === 'explotacion_lcm') ? true : f.applyIrpf }));
                  }}><option value="">Sin definir</option><option value="gestion">Gestión</option><option value="explotacion">Explotación</option><option value="explotacion_lcm">Explotación LCM</option><option value="rent2rent">Rent2Rent</option><option value="ambos">Ambos</option></select>
                  {(editForm.contractModel === 'explotacion' || editForm.contractModel === 'explotacion_lcm') && (
                    <label className="flex items-center gap-2 cursor-pointer text-sm mt-2">
                      <input type="checkbox" className="w-4 h-4 rounded" checked={editForm.applyIrpf ?? false} onChange={e => setEditForm(f => ({ ...f, applyIrpf: e.target.checked }))} />
                      Retención IRPF 19%
                    </label>
                  )}
                </div>
                <div><label className="text-xs text-sereno-dark/40">Fuente</label><select className="input text-sm" value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))}><option value="">Seleccionar...</option>{Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                {editForm.source && <div><label className="text-xs text-sereno-dark/40">Detalle fuente</label><input className="input text-sm" value={editForm.sourceDetail} onChange={e => setEditForm(f => ({ ...f, sourceDetail: e.target.value }))} /></div>}
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" className="w-4 h-4 rounded" checked={editForm.hasMultipleProperties} onChange={e => setEditForm(f => ({ ...f, hasMultipleProperties: e.target.checked }))} /> Múltiples propiedades</label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" className="w-4 h-4 rounded" checked={editForm.previouslyManagedRentals} onChange={e => setEditForm(f => ({ ...f, previouslyManagedRentals: e.target.checked }))} /> Gestionaba antes</label>
                </div>
                {editForm.previouslyManagedRentals && <div><label className="text-xs text-sereno-dark/40">Detalle gestión anterior</label><input className="input text-sm" value={editForm.previousManagementDetail} onChange={e => setEditForm(f => ({ ...f, previousManagementDetail: e.target.value }))} /></div>}
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-sereno-dark/40">Expectativa (€)</label><input type="number" className="input text-sm" value={editForm.incomeExpectation} onChange={e => setEditForm(f => ({ ...f, incomeExpectation: e.target.value }))} /></div>
                  <div><label className="text-xs text-sereno-dark/40">Período</label><select className="input text-sm" value={editForm.incomeExpectationPeriod} onChange={e => setEditForm(f => ({ ...f, incomeExpectationPeriod: e.target.value }))}><option value="monthly">Mensual</option><option value="annual">Anual</option></select></div>
                </div>
                <div><label className="text-xs text-sereno-dark/40">Etiquetas (separadas por coma)</label><input className="input text-sm" value={editForm.tags} onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))} placeholder="urgente, vip, extranjero" /></div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Notas</h3>
              {editSection !== 'notes' ? (
                <button onClick={() => startEdit('notes', { notes: owner.notes || '' })} className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors">
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              )}
            </div>
            {editSection !== 'notes' ? (
              <p className="text-sm whitespace-pre-wrap">{owner.notes || <span className="text-sereno-dark/25">Sin notas</span>}</p>
            ) : (
              <textarea className="input text-sm" rows={4} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
            )}
          </div>
          </div>
        )}

        {/* === TAB: FACTURACIÓN === */}
        {activeTab === 'tareas' && (
          <div className="card">
            <BloqueTareas
              relatedType="owner"
              relatedId={owner.id}
              relatedName={`${owner.firstName || ''} ${owner.lastName || ''}`.trim() || null}
              titulo="Tareas del propietario"
            />
          </div>
        )}

        {activeTab === 'facturacion' && (
          <div className="max-w-2xl">
          {/* Documentación */}
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Facturación</h3>
              {editSection !== 'billing' ? (
                <button onClick={() => startEdit('billing', {
                  invoicePrefix: owner.invoicePrefix || '',
                  invoiceNextNum: owner.invoiceNextNum ?? '',
                  guestInvoicePrefix: owner.guestInvoicePrefix || '',
                  guestInvoiceNextNum: owner.guestInvoiceNextNum ?? '',
                  commissionPercent: owner.commissionPercent ?? '',
                  fixedRent: owner.fixedRent ?? '',
                  applyIrpf: owner.applyIrpf ?? false,
                })} className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors">
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              )}
            </div>
            {editSection !== 'billing' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-sereno-dark/50">Serie factura</span>
                  <span className="font-mono">{owner.invoicePrefix || <span className="text-sereno-dark/25 font-sans">Sin configurar</span>}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sereno-dark/50">Nº siguiente factura</span>
                  {owner.invoiceNextNum != null
                    ? <span className="font-semibold tabular-nums">{owner.invoiceNextNum}</span>
                    : <span className="text-sereno-dark/25">Sin configurar</span>}
                </div>
                {owner.invoicePrefix && owner.invoiceNextNum != null && (
                  <div className="flex justify-between items-center">
                    <span className="text-sereno-dark/50">Próxima factura</span>
                    <span className="font-mono text-xs bg-sereno-blue/20 px-2 py-0.5 rounded">{owner.invoicePrefix}{owner.invoiceNextNum}</span>
                  </div>
                )}
                {owner.contractModel === 'gestion' && (
                  <>
                    <div className="border-t border-gray-100 pt-2">
                      <div className="flex justify-between">
                        <span className="text-sereno-dark/50">Serie fact. huésped</span>
                        <span className="font-mono">{owner.guestInvoicePrefix || <span className="text-sereno-dark/25 font-sans">Sin configurar</span>}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sereno-dark/50">Nº siguiente fact. huésped</span>
                      {owner.guestInvoiceNextNum != null
                        ? <span className="font-semibold tabular-nums">{owner.guestInvoiceNextNum}</span>
                        : <span className="text-sereno-dark/25">Sin configurar</span>}
                    </div>
                    {owner.guestInvoicePrefix && owner.guestInvoiceNextNum != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-sereno-dark/50">Próxima fact. huésped</span>
                        <span className="font-mono text-xs bg-sereno-blue/20 px-2 py-0.5 rounded">{owner.guestInvoicePrefix}{owner.guestInvoiceNextNum}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="border-t border-gray-100 pt-2">
                  <div className="flex justify-between">
                    <span className="text-sereno-dark/50">Comisión Sereno</span>
                    <span>{owner.commissionPercent != null ? `${owner.commissionPercent}%` : <span className="text-sereno-dark/25">Sin definir</span>}</span>
                  </div>
                  {owner.fixedRent != null && (
                    <div className="flex justify-between mt-2">
                      <span className="text-sereno-dark/50">Renta fija</span>
                      <span>{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(owner.fixedRent)}</span>
                    </div>
                  )}
                  {(owner.contractModel === 'explotacion' || owner.contractModel === 'explotacion_lcm') && (
                    <div className="flex justify-between mt-2">
                      <span className="text-sereno-dark/50">Retención IRPF</span>
                      <span className={owner.applyIrpf ? 'text-amber-700 font-medium' : 'text-sereno-dark/25'}>{owner.applyIrpf ? '19%' : 'No aplica'}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-sereno-dark/40">Serie factura</label>
                    <input className="input text-sm font-mono" value={editForm.invoicePrefix} onChange={e => setEditForm(f => ({ ...f, invoicePrefix: e.target.value }))} placeholder="SERENO26/" />
                  </div>
                  <div>
                    <label className="text-xs text-sereno-dark/40">Nº siguiente</label>
                    <input type="number" min="1" className="input text-sm" value={editForm.invoiceNextNum} onChange={e => setEditForm(f => ({ ...f, invoiceNextNum: e.target.value === '' ? '' : Number(e.target.value) }))} placeholder="1" />
                  </div>
                </div>
                {editForm.invoicePrefix && editForm.invoiceNextNum !== '' && (
                  <p className="text-xs text-sereno-dark/40">Próxima: <span className="font-mono text-sereno-dark/70">{editForm.invoicePrefix}{editForm.invoiceNextNum}</span></p>
                )}
                {owner.contractModel === 'gestion' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-sereno-dark/40">Serie fact. huésped</label>
                        <input className="input text-sm font-mono" value={editForm.guestInvoicePrefix} onChange={e => setEditForm(f => ({ ...f, guestInvoicePrefix: e.target.value }))} placeholder="H-" />
                      </div>
                      <div>
                        <label className="text-xs text-sereno-dark/40">Nº siguiente fact. huésped</label>
                        <input type="number" min="1" className="input text-sm" value={editForm.guestInvoiceNextNum} onChange={e => setEditForm(f => ({ ...f, guestInvoiceNextNum: e.target.value === '' ? '' : Number(e.target.value) }))} placeholder="1" />
                      </div>
                    </div>
                    {editForm.guestInvoicePrefix && editForm.guestInvoiceNextNum !== '' && (
                      <p className="text-xs text-sereno-dark/40">Próxima fact. huésped: <span className="font-mono text-sereno-dark/70">{editForm.guestInvoicePrefix}{editForm.guestInvoiceNextNum}</span></p>
                    )}
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-sereno-dark/40">Comisión Sereno (%)</label>
                    <input type="number" min="0" max="100" step="0.5" className="input text-sm" value={editForm.commissionPercent} onChange={e => setEditForm(f => ({ ...f, commissionPercent: e.target.value === '' ? '' : Number(e.target.value) }))} placeholder="20" />
                  </div>
                  <div>
                    <label className="text-xs text-sereno-dark/40">Renta fija (€)</label>
                    <input type="number" min="0" className="input text-sm" value={editForm.fixedRent} onChange={e => setEditForm(f => ({ ...f, fixedRent: e.target.value === '' ? '' : Number(e.target.value) }))} placeholder="Opcional" />
                  </div>
                </div>
                {(owner.contractModel === 'explotacion' || owner.contractModel === 'explotacion_lcm') && (
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" className="w-4 h-4 rounded" checked={editForm.applyIrpf ?? false} onChange={e => setEditForm(f => ({ ...f, applyIrpf: e.target.checked }))} />
                    Retención IRPF 19%
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Segundo propietario (propiedad compartida) */}
          <div className="card space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-sereno-dark/40" />
                <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Segundo propietario</h3>
              </div>
              {editSection !== 'secondOwner' ? (
                <button onClick={() => startEdit('secondOwner', {
                  secondOwnerEnabled: owner.secondOwnerEnabled ?? false,
                  secondOwnerName: owner.secondOwnerName || '',
                  secondOwnerDni: owner.secondOwnerDni || '',
                  secondOwnerBillingAddress: owner.secondOwnerBillingAddress || '',
                  secondOwnerEmail: owner.secondOwnerEmail || '',
                  secondOwnerIban: owner.secondOwnerIban || '',
                  secondOwnerPercent: owner.secondOwnerPercent ?? 50,
                })} className="p-1.5 rounded-lg hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60 transition-colors">
                  <Pencil size={14} />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={cancelEdit} className="p-1.5 rounded-lg hover:bg-gray-100 text-sereno-dark/30 hover:text-sereno-dark/60"><X size={14} /></button>
                  <button onClick={saveSection} disabled={saving} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                </div>
              )}
            </div>
            {editSection !== 'secondOwner' ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-sereno-dark/50">Facturación a dos propietarios</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${owner.secondOwnerEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-sereno-dark/40'}`}>
                    {owner.secondOwnerEnabled ? 'Activada' : 'Desactivada'}
                  </span>
                </div>
                {owner.secondOwnerEnabled ? (
                  <div className="border-t border-gray-100 pt-2 space-y-2">
                    <div className="flex justify-between gap-4">
                      <span className="text-sereno-dark/50 shrink-0">Nombre completo</span>
                      <span className="text-right">{owner.secondOwnerName || <span className="text-sereno-dark/25">Sin configurar</span>}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sereno-dark/50 shrink-0">DNI/CIF</span>
                      <span className="font-mono text-right">{owner.secondOwnerDni || <span className="text-sereno-dark/25 font-sans">Sin configurar</span>}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-sereno-dark/50 shrink-0">Dirección de facturación</span>
                      <span className="text-right whitespace-pre-wrap">{owner.secondOwnerBillingAddress || <span className="text-sereno-dark/25">Sin configurar</span>}</span>
                    </div>
                    {owner.secondOwnerEmail && (
                      <div className="flex justify-between gap-4">
                        <span className="text-sereno-dark/50 shrink-0">Email</span>
                        <a href={`mailto:${owner.secondOwnerEmail}`} className="text-right hover:underline break-all">{owner.secondOwnerEmail}</a>
                      </div>
                    )}
                    {owner.secondOwnerIban && (
                      <div className="flex justify-between gap-4">
                        <span className="text-sereno-dark/50 shrink-0">IBAN</span>
                        <span className="font-mono text-right break-all">{owner.secondOwnerIban}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-4">
                      <span className="text-sereno-dark/50 shrink-0">Reparto</span>
                      <span className="tabular-nums">{100 - (owner.secondOwnerPercent ?? 50)}% / {owner.secondOwnerPercent ?? 50}%</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-sereno-dark/40">
                    Actívala si la propiedad es compartida y las facturas deben emitirse a nombre de dos propietarios.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded"
                    checked={editForm.secondOwnerEnabled ?? false}
                    onChange={e => setEditForm(f => ({ ...f, secondOwnerEnabled: e.target.checked }))}
                  />
                  Activar facturación para dos propietarios
                </label>
                {editForm.secondOwnerEnabled && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-sereno-dark/40">Nombre completo</label>
                        <input className="input text-sm" value={editForm.secondOwnerName} onChange={e => setEditForm(f => ({ ...f, secondOwnerName: e.target.value }))} placeholder="Nombre y apellidos" />
                      </div>
                      <div>
                        <label className="text-xs text-sereno-dark/40">DNI/CIF</label>
                        <input className="input text-sm font-mono" value={editForm.secondOwnerDni} onChange={e => setEditForm(f => ({ ...f, secondOwnerDni: e.target.value }))} placeholder="12345678A" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-sereno-dark/40">Dirección de facturación</label>
                      <textarea className="input text-sm" rows={2} value={editForm.secondOwnerBillingAddress} onChange={e => setEditForm(f => ({ ...f, secondOwnerBillingAddress: e.target.value }))} placeholder="Calle, nº, CP, ciudad, provincia" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-sereno-dark/40">Email</label>
                        <input type="email" className="input text-sm" value={editForm.secondOwnerEmail} onChange={e => setEditForm(f => ({ ...f, secondOwnerEmail: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-sereno-dark/40">IBAN</label>
                        <input className="input text-sm font-mono" value={editForm.secondOwnerIban} onChange={e => setEditForm(f => ({ ...f, secondOwnerIban: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-sereno-dark/40">% del segundo propietario</label>
                        <input type="number" min="0" max="100" step="0.5" className="input text-sm" value={editForm.secondOwnerPercent} onChange={e => setEditForm(f => ({ ...f, secondOwnerPercent: e.target.value === '' ? '' : Number(e.target.value) }))} placeholder="50" />
                      </div>
                      <div className="flex items-end pb-2">
                        <p className="text-xs text-sereno-dark/40">
                          Titular 1: <span className="tabular-nums text-sereno-dark/70">{100 - (Number(editForm.secondOwnerPercent) || 0)}%</span> · Titular 2: <span className="tabular-nums text-sereno-dark/70">{Number(editForm.secondOwnerPercent) || 0}%</span>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          </div>
        )}

        {/* === TAB: DOCUMENTOS === */}
        {activeTab === 'documentos' && (
          <div className="max-w-2xl">
          {/* Documentación */}
          <div className="card space-y-4">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Documentación</h3>
            <DocumentSection title="Licencia turística" ownerId={id!} category="licencia-turistica" documents={ownerDocs} onRefresh={refreshDocs} />
            <div className="border-t border-gray-100" />
            <DocumentSection title="Otros" ownerId={id!} category="otros" documents={ownerDocs} onRefresh={refreshDocs} />
          </div>
          </div>
        )}

        {/* === TAB: PORTAL === */}
        {activeTab === 'portal' && (
          <div className="max-w-2xl">
          {/* Portal del Propietario */}
          <div className="card space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound size={14} className="text-sereno-dark/40" />
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Portal del Propietario</h3>
              {/* Enlace y no botón: el servidor redirige al portal ya dentro como el propietario (solo lectura) */}
              <a
                href={`/api/owner-portal/${id}/impersonate`}
                target="_blank"
                rel="noopener noreferrer"
                title="Abre el portal en otra pestaña tal y como lo ve el propietario, en solo lectura"
                className="ml-auto btn-secondary text-sm flex items-center gap-1.5"
              >
                <ExternalLink size={13} />
                Ver como propietario
              </a>
            </div>

            {!portalStatus.enabled && !portalPassword ? (
              <ConfirmarActivacion
                ownerId={id!}
                nombre={[owner.firstName, owner.lastName].filter(Boolean).join(' ')}
                estado={portalStatus}
                onActivado={recargarPortal}
              />
            ) : (
              <div className="space-y-3">
                {/* Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-sereno-dark/50">Acceso activo</span>
                  <button
                    onClick={async () => {
                      setPortalLoading(true);
                      try {
                        const result = await api.togglePortal(id!, !portalStatus.enabled);
                        setPortalStatus(prev => ({ ...prev, enabled: result.enabled }));
                      } catch (err: any) { alert(err.message || 'Error'); }
                      finally { setPortalLoading(false); }
                    }}
                    disabled={portalLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${portalStatus.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${portalStatus.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {/* Usuario */}
                <div className="flex justify-between text-sm">
                  <span className="text-sereno-dark/50">Usuario</span>
                  <span className="font-mono text-xs">{portalStatus.email || owner.email}</span>
                </div>
                {(owner.emailSecondary || portalStatus.emailSecondary) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-sereno-dark/50">Email alt.</span>
                    <span className="font-mono text-xs">{portalStatus.emailSecondary || owner.emailSecondary}</span>
                  </div>
                )}

                <EstadoAcceso ownerId={id!} estado={portalStatus} onCambio={recargarPortal} />

                {/* Contraseña (solo visible tras resetear) */}
                {portalPassword && (
                  <div className="space-y-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-700 font-medium">Contraseña generada — cópiala ahora, no se volverá a mostrar.</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-1.5 rounded border text-sm font-mono">
                        {portalShowPassword ? portalPassword : '••••••••••••'}
                      </code>
                      <button
                        onClick={() => setPortalShowPassword(v => !v)}
                        className="p-1.5 rounded hover:bg-amber-100 text-amber-700"
                        title={portalShowPassword ? 'Ocultar' : 'Mostrar'}
                      >
                        {portalShowPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(portalPassword);
                          setPortalCopied(true);
                          setTimeout(() => setPortalCopied(false), 2000);
                        }}
                        className="p-1.5 rounded hover:bg-amber-100 text-amber-700"
                        title="Copiar"
                      >
                        {portalCopied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Password actions */}
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  {/* Random reset */}
                  <button
                    onClick={async () => {
                      if (!confirm('¿Generar una nueva contraseña aleatoria? La anterior dejará de funcionar.')) return;
                      setPortalLoading(true);
                      try {
                        const result = await api.resetPortalPassword(id!);
                        setPortalPassword(result.password);
                        setPortalShowPassword(true);
                        setPortalCopied(false);
                        setPortalShowCustomInput(false);
                      } catch (err: any) { alert(err.message || 'Error al resetear contraseña'); }
                      finally { setPortalLoading(false); }
                    }}
                    disabled={portalLoading}
                    className="text-sm text-sereno-dark/50 hover:text-sereno-dark flex items-center gap-1.5 transition-colors w-full"
                  >
                    {portalLoading ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                    Generar contraseña aleatoria
                  </button>

                  {/* Custom password toggle */}
                  <button
                    onClick={() => setPortalShowCustomInput(v => !v)}
                    className="text-sm text-sereno-dark/50 hover:text-sereno-dark flex items-center gap-1.5 transition-colors w-full"
                  >
                    <KeyRound size={13} />
                    Establecer contraseña personalizada
                  </button>

                  {portalShowCustomInput && (
                    <div className="flex gap-2 pt-1">
                      <input
                        type="text"
                        className="input text-sm flex-1 font-mono"
                        placeholder="Mín. 6 caracteres"
                        value={portalCustomPassword}
                        onChange={e => setPortalCustomPassword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && e.currentTarget.form?.requestSubmit?.()}
                      />
                      <button
                        onClick={async () => {
                          if (portalCustomPassword.trim().length < 6) {
                            alert('La contraseña debe tener al menos 6 caracteres.');
                            return;
                          }
                          if (!confirm(`¿Establecer la contraseña "${portalCustomPassword}"? La anterior dejará de funcionar.`)) return;
                          setPortalLoading(true);
                          try {
                            const result = await api.resetPortalPassword(id!, portalCustomPassword.trim());
                            setPortalPassword(result.password);
                            setPortalShowPassword(true);
                            setPortalCopied(false);
                            setPortalCustomPassword('');
                            setPortalShowCustomInput(false);
                          } catch (err: any) { alert(err.message || 'Error al establecer contraseña'); }
                          finally { setPortalLoading(false); }
                        }}
                        disabled={portalLoading || portalCustomPassword.trim().length < 6}
                        className="btn-secondary text-sm px-3 flex items-center gap-1 disabled:opacity-40"
                      >
                        {portalLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        Guardar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        )}

        {/* === TAB: PROPIEDADES === */}
        {activeTab === 'propiedades' && (
          <div>
          {/* Properties */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                Propiedades ({properties?.length || 0})
              </h3>
              <button onClick={openPropertyPicker} className="btn-secondary text-sm flex items-center gap-1">
                <Plus size={14} /> Añadir propiedad
              </button>
            </div>
            {properties && properties.length > 0 ? (
              <div className="space-y-3">
                {properties.map(prop => (
                  <div
                    key={prop.id}
                    onClick={() => navigate(`/properties/${prop.id}`)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-sereno-blue/20 cursor-pointer transition-colors"
                  >
                    <Building2 size={18} className="text-sereno-dark/40" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{prop.nickname || prop.name}</p>
                      <p className="text-xs text-sereno-dark/40">{prop.registrationType} · {ZONE_LABELS[prop.address?.zone] || prop.address?.zone}</p>
                    </div>
                    <button
                      onClick={(e) => handleUnassignProperty(prop.id, e)}
                      className="p-1.5 rounded-md text-sereno-dark/30 hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Desasociar propiedad"
                    >
                      <Unlink size={15} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sereno-dark/40">Sin propiedades</p>
            )}
          </div>
          </div>
        )}

        {/* === TAB: LIQUIDACIONES === */}
        {activeTab === 'liquidaciones' && (
          <OwnerLiquidaciones owner={owner} liquidaciones={ownerLiquidaciones} loading={loadingLiquidaciones} />
        )}

        {/* === TAB: CONTRATOS === */}
        {activeTab === 'contratos' && (
          <div className="space-y-6">
          {/* Contratos generados desde el CRM */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                Contratos generados ({contratosGenerados.length})
              </h3>
              <button onClick={() => navigate(`/contracts/nuevo?tipo=owner&id=${id}`)} className="btn-primary text-sm flex items-center gap-1">
                <Plus size={14} /> Generar contrato
              </button>
            </div>
            {contratosGenerados.length > 0 ? (
              <div className="space-y-2">
                {contratosGenerados.map(c => (
                  <div
                    key={c.id}
                    onClick={() => navigate(`/contracts/c/${c.id}`)}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-sereno-blue/20 cursor-pointer transition-colors group"
                  >
                    <FileText size={18} className="text-sereno-dark/40" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        <span className="font-mono text-xs mr-2">{c.codigo}</span>
                        {NOMBRE_MODELO[c.templateKey]} · v{c.templateVersion}
                        {c.preajusteNombre && <span className="text-sereno-dark/50"> · {c.preajusteNombre}</span>}
                      </p>
                      <p className="text-xs text-sereno-dark/40">{formatDate(c.generadoAt || c.createdAt)}</p>
                    </div>
                    <span className={`badge ${ESTADO_CONTRATO[c.estado].className}`}>{ESTADO_CONTRATO[c.estado].label}</span>
                    {c.pdf && (
                      <a
                        href={api.contratoPdfUrl(c.id, { download: true })}
                        onClick={e => e.stopPropagation()}
                        className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/30 hover:text-sereno-dark/70 transition-colors"
                        title="Descargar PDF"
                      >
                        <Download size={14} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sereno-dark/40">Ningún contrato generado todavía. El PDF que se genere aquí queda también en Documentos › Contrato.</p>
            )}
          </div>

          {/* Contracts */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                Firmas en Click &amp; Sign ({ownerContracts.length})
              </h3>
              <button onClick={openContractPicker} className="btn-secondary text-sm flex items-center gap-1">
                <Plus size={14} /> Vincular contrato
              </button>
            </div>
            {ownerContracts.length > 0 ? (
              <div className="space-y-3">
                {ownerContracts.map((sig: any) => (
                  <div
                    key={sig.signature_id}
                    onClick={() => navigate('/contracts/firmas')}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-sereno-blue/20 cursor-pointer transition-colors group"
                  >
                    <FileSignature size={18} className="text-sereno-dark/40" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{sig.contract_id || `#${sig.signature_id}`}</p>
                      <p className="text-xs text-sereno-dark/40">
                        {sig.config_name || 'Sin configuración'} · {sig.signature_status}
                      </p>
                    </div>
                    {/* Una sola fila de acciones, todas de 28 px: «Procesar contrato» con texto porque es la
                        que se busca, y las otras tres en icono, visibles siempre como la descarga
                        de «Contratos generados». */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      {sig.signature_status === 'signed' && (
                        <>
                          <button
                            onClick={e => { e.stopPropagation(); setProcesando(String(sig.signature_id)); }}
                            className="h-7 inline-flex items-center gap-1.5 px-2 rounded-md text-xs font-medium text-sereno-dark/60 hover:text-sereno-dark hover:bg-sereno-blue/30 transition-colors"
                            title="Pasar a la ficha los datos que rellenó al firmar"
                            aria-label="Procesar contrato"
                          >
                            <ScanText size={14} /> <span className="hidden sm:inline">Procesar contrato</span>
                          </button>
                          <span className="h-4 border-l border-gray-200 mx-1" aria-hidden />
                        </>
                      )}
                      <button
                        onClick={e => { e.stopPropagation(); handleViewContract(sig); }}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-sereno-blue/30 text-sereno-dark/30 hover:text-sereno-dark/70 transition-colors"
                        title="Vista rápida"
                        aria-label="Vista rápida"
                      >
                        <Eye size={14} />
                      </button>
                      <a
                        href={api.signaturePdfUrl(String(sig.signature_id), { download: true })}
                        onClick={e => e.stopPropagation()}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-sereno-blue/30 text-sereno-dark/30 hover:text-sereno-dark/70 transition-colors"
                        title="Descargar"
                        aria-label="Descargar"
                      >
                        <Download size={14} />
                      </a>
                      <button
                        onClick={e => { e.stopPropagation(); handleUnlinkContract(String(sig.signature_id)); }}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-red-100 text-sereno-dark/30 hover:text-red-600 transition-colors"
                        title="Desvincular"
                        aria-label="Desvincular"
                      >
                        <Unlink size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sereno-dark/40">Sin contratos vinculados</p>
            )}
            <div className="border-t border-gray-100 mt-4 pt-4">
              <DocumentSection title="Documentos de contrato" ownerId={id!} category="contrato" documents={ownerDocs} onRefresh={refreshDocs} />
            </div>
          </div>
          </div>
        )}

        </div>

      <Modal isOpen={showPropertyPicker} onClose={() => setShowPropertyPicker(false)} title="Añadir propiedad" size="lg">
        <div className="space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Buscar propiedad por nombre o dirección..."
              value={propertySearch}
              onChange={e => handleSearchProperties(e.target.value)}
              autoFocus
            />
          </div>
          {loadingProperties ? (
            <div className="py-8"><LoadingSpinner /></div>
          ) : unassignedProperties.length > 0 ? (
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg">
              {unassignedProperties.map(prop => (
                <button
                  key={prop.id}
                  onClick={() => handleAssignProperty(prop.id)}
                  disabled={assigningProperty}
                  className="w-full text-left px-4 py-3 hover:bg-sereno-blue/20 transition-colors flex items-center gap-3"
                >
                  <Building2 size={18} className="text-sereno-dark/40 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{prop.nickname || prop.name}</p>
                    <p className="text-xs text-sereno-dark/40 truncate">{prop.registrationType} · {ZONE_LABELS[prop.address?.zone] || prop.address?.zone} · {prop.address?.street}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-sereno-dark/40 text-center py-6">No hay propiedades sin asignar</p>
          )}
        </div>
      </Modal>

      <Modal isOpen={!!procesando} onClose={() => cerrarProcesado()} title="Procesar contrato" size="lg">
        {procesando && (
          <ProcesarContrato
            signatureId={procesando}
            onCerrar={() => cerrarProcesado()}
            onGuardado={() => setProcesadoGuardado(true)}
            onVerFicha={() => cerrarProcesado(true)}
          />
        )}
      </Modal>

      <Modal isOpen={showContractPicker} onClose={() => setShowContractPicker(false)} title="Vincular contrato" size="lg">
        <div className="space-y-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Buscar contrato..."
              value={contractSearch}
              onChange={e => setContractSearch(e.target.value)}
              autoFocus
            />
          </div>
          {loadingContracts ? (
            <div className="py-8"><LoadingSpinner /></div>
          ) : (() => {
            const filtered = availableContracts.filter((s: any) => {
              if (!contractSearch) return true;
              const q = contractSearch.toLowerCase();
              const sigText = [
                s.contract_id, s.config_name, String(s.signature_id),
                ...(s.signatories || []).map((st: any) => `${st.name} ${st.surname} ${st.email}`)
              ].join(' ').toLowerCase();
              return sigText.includes(q);
            });
            return filtered.length > 0 ? (
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg">
                {filtered.map((sig: any) => (
                  <button
                    key={sig.signature_id}
                    onClick={() => handleLinkContract(sig)}
                    disabled={linkingContract}
                    className="w-full text-left px-4 py-3 hover:bg-sereno-blue/20 transition-colors flex items-center gap-3"
                  >
                    <FileSignature size={18} className="text-sereno-dark/40 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{sig.contract_id || `#${sig.signature_id}`}</p>
                      <p className="text-xs text-sereno-dark/40 truncate">
                        {sig.config_name || 'Sin configuración'} · {sig.signature_status}
                        {sig.signatories?.map((st: any) => ` · ${st.name} ${st.surname}`).join('')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sereno-dark/40 text-center py-6">No hay contratos disponibles</p>
            );
          })()}
        </div>
      </Modal>

      {/* PDF Viewer modal */}
      {pdfViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPdfViewer(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] mx-4 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-semibold text-sereno-dark truncate">{pdfViewer.filename}</h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={api.signaturePdfUrl(pdfViewer.signatureId, { download: true })}
                  className="btn-secondary text-sm flex items-center gap-1"
                >
                  <Download size={14} /> Descargar
                </a>
                <button onClick={() => setPdfViewer(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                  <XCircle size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={api.signaturePdfUrl(pdfViewer.signatureId)}
                className="w-full h-full border-0"
                title="PDF Viewer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
